import { MASTERY_MAX } from '../../types'
import { useLocale } from '../../i18n/context'

// Single-hue magnitude ramp — NEVER red (DESIGN_SYSTEM "Wave 3" §1/§3).
function fillFor(level: number): string {
  if (level >= 6) return 'var(--mastery-fill-high)'
  if (level >= 4) return 'var(--mastery-fill-mid)'
  return 'var(--mastery-fill-low)'
}

interface Props {
  /** Mastery level 1–8, or `null` when no `MasteryState` row exists yet — a
   * card genuinely never studied. `null` is its own state (empty meter, its
   * own caption), not "level 1": a card that has never been shown has not
   * earned even the ladder's first rung. */
  level: number | null
  /** Terminal state (ADR-047 §4) — never served again. Takes priority over
   * `level`. */
  isMastered?: boolean
  /** Renders the visible fraction beside the bar (desktop deck row only —
   * every other caller keeps the bar-only look it has always had, so this
   * defaults to `false` rather than becoming a silent visual change for
   * `VocabCard`'s mobile row or the design-system demo page). When `false`,
   * the same information is still available via `title`/`aria-label`. */
  showStatus?: boolean
  /** Track length — a number is px, a string is any CSS length. Defaults to
   * `--mastery-meter-width`, the deck-row density; `VocabCard` and the demo
   * page pass their own. */
  width?: number | string
  className?: string
}

/**
 * Per-word mastery: eight discrete gap-separated segments (DESIGN_SYSTEM "Wave 3" §1
 * amendment 2026-07-05), optionally with the fraction printed beside them.
 *
 * **Every segment is drawn, filled or not** — each carries a `--mastery-tick`
 * hairline and a `--mastery-track` bed, so an unfilled cell is an object in
 * its own right rather than the absence of one. This is the fix for the
 * failure the bar had at both ends of its range: at 1/8 a lone filled cell
 * floated in a strip that was otherwise nearly invisible, and a never-studied
 * card's meter was indistinguishable from empty space, so the two states the
 * reader most needs to spot were the two the bar communicated worst. Note
 * this is NOT the "ticks over a continuous fill" model the 2026-07-05
 * amendment retired: there is still no continuous strip underneath, the
 * gutters between cells are still transparent, and the hairline outlines each
 * discrete cell rather than being ruled across a bar.
 *
 * The three states, when `showStatus` is on:
 *  - never studied (`level === null`): every segment empty-but-drawn, and no
 *    fraction — there is no level to state, and the outlined-empty bar now
 *    says "nothing here yet" on its own. This is the one state with no
 *    number to fall back on, so `tr.mastery.neverStudied` (a distinct phrase,
 *    not a ladder word — there is no per-level word) still goes to
 *    `aria-label`/`title`.
 *  - in progress: filled bar plus the bare fraction ("N/8") — the bar and
 *    fraction already say where the card is, and there is no ladder word to
 *    add and no review interval to print (a number the reader cannot act on).
 *  - mastered: full bar, and the fraction shown as well ("8/8"). Beneath the
 *    bar this line was redundant and was suppressed; beside it, in a fixed
 *    numeral column, printing it is what keeps the column from going ragged.
 *    `aria-label` is the same fraction — mastered is not a word, it is the
 *    top of the same numeral scale every other level uses.
 *
 * The Learn animated mastery dot (ADR-014, `MasteryBadge`) is a separate signal
 * and does NOT use this component.
 */
export default function MasteryMeter({
  level, isMastered = false, showStatus = false,
  width = 'var(--mastery-meter-width)', className,
}: Props) {
  const { tr } = useLocale()

  const clamped = level === null ? 0 : Math.max(1, Math.min(MASTERY_MAX, Math.round(level)))
  const filled = isMastered ? MASTERY_MAX : clamped
  const fill = fillFor(Math.max(1, filled))

  // The number beside the bar. Absent only for never-studied, which has no
  // level to print — see the class doc.
  const fraction = level === null && !isMastered ? null : `${filled}/${MASTERY_MAX}`
  // The bar's accessible name. Never-studied has no number, so it falls back
  // to the one phrase that names a state rather than a level; every other
  // state states the same fraction a sighted reader sees — mastered included,
  // since "mastered" is the top of this numeral scale, not a separate word.
  const fullLabel = fraction ?? tr.mastery.neverStudied

  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}
    >
      {showStatus && (
        // Fixed numeral column so the bars line up down the list regardless of
        // whether a given row prints a fraction at all. `ch` (not a px width)
        // because what has to fit is exactly "N/8" in the current face at the
        // current size — it tracks the type, not a guess about it.
        <span
          aria-hidden="true"
          style={{
            minWidth: '3ch', textAlign: 'right', flexShrink: 0,
            fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--text-secondary)', lineHeight: 1,
          }}
        >
          {fraction}
        </span>
      )}
      <div
        role="img"
        aria-label={fullLabel}
        title={fullLabel}
        style={{
          display: 'flex', gap: 'var(--mastery-meter-gap)',
          width, height: 'var(--mastery-meter-height)', flexShrink: 0,
        }}
      >
        {Array.from({ length: MASTERY_MAX }, (_, i) => {
          const isFilled = i < filled
          return (
            <div
              key={i}
              aria-hidden="true"
              style={{
                flex: 1,
                height: '100%',
                boxSizing: 'border-box',
                borderRadius: 'var(--mastery-meter-radius)',
                // The outline is what makes an empty cell readable. A filled
                // cell outlines in its own fill so the two read as the same
                // shape at the same size — the difference between them is
                // the bed, never the geometry.
                border: `1px solid ${isFilled ? fill : 'var(--mastery-tick)'}`,
                background: isFilled ? fill : 'var(--mastery-track)',
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
